/**
 * Formatadores e utilitários compartilhados entre cupomDelivery, cupomFechamento, cupomCaixa, cupomPreparo.
 * Centraliza: formatação de moeda/CNPJ/telefone/data, logo HTML, cache de empresa e impressão.
 */

import { formatarQtd, medidaDoItem } from "../lib/unidadeVenda";
import { getTenantSlug } from "./tenantScope";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// ─── Cache compartilhado (invalidado quando o tenant muda) ──────────
let _companyCache = null;
let _companyCacheTime = 0;
let _impressaoCache = {};
let _impressaoCacheTime = 0;
let _cacheSlug = null;
const CACHE_TTL = 60000; // 60s

function _ensureSameTenant() {
  const slug = getTenantSlug();
  if (_cacheSlug && _cacheSlug !== slug) {
    _companyCache = null;
    _companyCacheTime = 0;
    _impressaoCache = {};
    _impressaoCacheTime = 0;
  }
  _cacheSlug = slug;
}

// ─── Formatadores ───────────────────────────────────────────────────

export function fmtMoeda(v) {
  return (Number(v) || 0).toFixed(2).replace(".", ",");
}

export function fmtCnpj(raw) {
  const d = String(raw || "").replace(/\D/g, "");
  if (d.length !== 14) return raw || "";
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

export function fmtTelefone(raw) {
  const d = String(raw ?? "").replace(/\D/g, "");
  if (d.length === 0) return "\u2014";
  let d2 = d;
  if ((d.length === 12 || d.length === 13) && d.startsWith("55")) d2 = d.slice(2);
  if (d2.length === 11 && d2[2] === "9")
    return `(${d2.slice(0, 2)}) ${d2[2]}.${d2.slice(3, 7)}-${d2.slice(7)}`;
  if (d2.length === 10) return `(${d2.slice(0, 2)}) ${d2.slice(2, 6)}-${d2.slice(6)}`;
  if (d2.length === 9 && d2[0] === "9") return `${d2[0]}.${d2.slice(1, 5)}-${d2.slice(5)}`;
  if (d2.length === 8) return `${d2.slice(0, 4)}-${d2.slice(4)}`;
  return raw != null && String(raw).trim() !== "" ? String(raw) : "\u2014";
}

export function formatCupomDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * Formata `scheduled_at` (ISO local) como janela de 30min pro cupom térmico.
 * Ex.: "2026-07-05T19:00:00" → "SAB 05/07 · 19:00 - 19:30".
 * Retorna string vazia se ISO inválido/ausente.
 */
export function formatCupomAgendamento(iso) {
  if (!iso) return "";
  try {
    const [d, hm] = String(iso).split("T");
    const [y, mo, da] = d.split("-").map((n) => parseInt(n, 10));
    const [hh, mm] = (hm || "00:00").split(":").map((n) => parseInt(n, 10));
    const dt = new Date(y, mo - 1, da);
    if (isNaN(dt.getTime())) return "";
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diff = Math.round((dt - today) / (1000 * 60 * 60 * 24));
    let dayLbl;
    if (diff === 0) dayLbl = "HOJE";
    else if (diff === 1) dayLbl = "AMANHA";
    else {
      const WD = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SAB"];
      dayLbl = `${WD[dt.getDay()]} ${String(da).padStart(2, "0")}/${String(mo).padStart(2, "0")}`;
    }
    const startMin = hh * 60 + mm;
    const endMin = startMin + 30;
    const eh = String(Math.floor(endMin / 60) % 24).padStart(2, "0");
    const em = String(endMin % 60).padStart(2, "0");
    const shh = String(hh).padStart(2, "0");
    const smm = String(mm).padStart(2, "0");
    return `${dayLbl} · ${shh}:${smm} - ${eh}:${em}`;
  } catch {
    return "";
  }
}

export function formatCupomTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

// ─── Logo ───────────────────────────────────────────────────────────

export function buildLogoUrl(company) {
  if (!company?.logo_url) return "";
  if (company.logo_url.startsWith("http")) return company.logo_url;
  return window.location.origin + (company.logo_url.startsWith("/") ? company.logo_url : "/" + company.logo_url);
}

/**
 * Gera HTML do logo para cupom (com onerror fallback).
 * @param {string} logoUrl - URL completa do logo
 * @param {number} [side=97] - Tamanho em px (default: Math.round(96 * 1.0075))
 */
export function buildLogoHtml(logoUrl, side) {
  const s = side || Math.round(96 * 1.0075);
  if (!logoUrl) return "";
  return `<img src="${logoUrl}" alt="Logo" style="width:${s}px;height:${s}px;object-fit:cover;display:block;margin:0 auto 8px;" onerror="this.style.display='none';" />`;
}

// ─── Fetch empresa com cache ────────────────────────────────────────

/**
 * Busca dados da empresa com cache compartilhado (60s TTL).
 * Retorna objeto com company_name, slogan, address, cnpj, logo_url, phone, etc.
 */
export async function getEmpresaData() {
  _ensureSameTenant();
  const now = Date.now();
  if (_companyCache && (now - _companyCacheTime) < CACHE_TTL) {
    return _companyCache;
  }
  try {
    const tok = localStorage.getItem("token");
    const res = await fetch(`${API_BASE}/api/company/settings`, {
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      signal: AbortSignal.timeout(5000),
    });
    if (res.ok) {
      const data = await res.json();
      _companyCache = data;
      _companyCacheTime = now;
      return data;
    }
  } catch (_) {}
  return _companyCache || {};
}

/**
 * Busca config de impressão com cache compartilhado (60s TTL).
 * Retorna objeto com chaves: producao, preparo, caixa, fechamento, etc.
 */
export async function getImpressaoConfig() {
  _ensureSameTenant();
  const now = Date.now();
  if (_impressaoCache && _impressaoCacheTime && (now - _impressaoCacheTime) < CACHE_TTL) {
    return _impressaoCache;
  }
  try {
    const tok = localStorage.getItem("token");
    const res = await fetch(`${API_BASE}/api/settings/impressao`, {
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      signal: AbortSignal.timeout(5000),
    });
    if (res.ok) {
      const data = await res.json();
      _impressaoCache = data;
      _impressaoCacheTime = now;
      return data;
    }
  } catch (_) {}
  return _impressaoCache || {};
}

/**
 * Invalida o cache local de impressao_config — chamar apos PUT /api/settings/impressao
 * pra que a proxima impressao no mesmo dispositivo ja use o novo font_scale.
 * Os outros dispositivos do tenant vao pegar o novo valor naturalmente apos o TTL do cache.
 */
export function invalidateImpressaoCache() {
  _impressaoCache = {};
  _impressaoCacheTime = 0;
}

// ─── Cache de produtos (compartilhado entre cupomPreparo, cupomTicket) ─────
let _productsCacheCommon = null;
let _productsCacheCommonTime = 0;
let _productsCacheCommonSlug = null;

/**
 * Busca lista de produtos com cache (2 min TTL).
 * Usado pra inspecionar print_config (preparo_modules, ticket_*) por produto.
 */
export async function getAllProductsCached() {
  _ensureSameTenant();
  const slug = (typeof window !== 'undefined' && window.location?.pathname?.match(/\/admin\/([^/]+)/)?.[1]) || 'default';
  if (_productsCacheCommonSlug && _productsCacheCommonSlug !== slug) {
    _productsCacheCommon = null;
    _productsCacheCommonTime = 0;
  }
  _productsCacheCommonSlug = slug;
  const now = Date.now();
  if (_productsCacheCommon && (now - _productsCacheCommonTime) < CACHE_TTL * 2) return _productsCacheCommon;
  try {
    const tok = localStorage.getItem("token");
    const res = await fetch(`${API_BASE}/api/products`, {
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      signal: AbortSignal.timeout(10000),
    });
    if (res.ok) {
      _productsCacheCommon = await res.json();
      _productsCacheCommonTime = now;
      return _productsCacheCommon;
    }
  } catch (_) {}
  return _productsCacheCommon || [];
}

/**
 * Le print_config de um produto (parsing JSON safe).
 */
export function getProductPrintConfig(product) {
  if (!product) return {};
  try {
    return typeof product.print_config === 'string'
      ? JSON.parse(product.print_config || '{}')
      : (product.print_config || {});
  } catch (_) { return {}; }
}

/**
 * Helper de exibicao do nome de item PIZZA no cupom.
 * PizzaPopup monta "Pizza Grande · 4 Queijos" (1 sabor) ou
 * "Pizza Grande · 2 sabores" (2+ sabores), com separador " · ".
 * No cupom o sabor ja aparece como subitem -> o sufixo do pai vira redundante.
 *
 * Retorna:
 *   - 1 sabor:    "Pizza Grande" (strip do " · ...")
 *   - 2+ sabores: "Pizza Grande N Sabores"
 *   - Nao-pizza:  nome original
 */
export function pizzaDisplayName(item) {
  const nome = item.nome || item.name || "Item";
  const subitems = item.subitems || [];
  const sabores = subitems.filter(s =>
    (s.step_name || s.stepName || "").toLowerCase() === "sabores"
  );
  if (sabores.length === 0) return nome;

  // 1) Caminho rapido: separador " · " (formato atual do PizzaPopup).
  const sepIdx = nome.indexOf(" · ");
  if (sepIdx > 0) {
    const base = nome.slice(0, sepIdx);
    return sabores.length === 1 ? base : `${base} ${sabores.length} Sabores`;
  }

  // Fallback so pra 1 sabor (legacy/edge case sem " · "):
  if (sabores.length === 1) {
    const flavor = String(sabores[0].nome || sabores[0].name || "").replace(/^\d+\/?\d*\s+/, "").trim();
    const suffix = " " + flavor;
    if (flavor && nome.endsWith(suffix)) return nome.slice(0, -suffix.length);
  }
  return nome;
}

/**
 * Verifica se um produto deve gerar cupom de PREPARO no modulo informado.
 * Default (sem config): true (todos os modulos imprimem). Fallback seguro.
 */
// Quantidade no cupom (pedido do dono, 29/09/2026): peso e volume saem
// "1,200 kg" / "0,500 L" (sempre 3 casas); por unidade continua "2x". A unidade
// vem do cadastro (`produtos`) ou do item. Mesma regra do servidor
// (backend/api/v1/print/unidade.py) e do Núcleo Desktop. Desde 30/09/2026 só
// com o produto divisível (lib/unidadeVenda): "Açaí 500 ml" em litro sai "1x".
export { medidaDoItem };

// Sem medida, a quantidade sai com vírgula (30/09/2026): "0,5x", não "0.5x".
export function quantidadeNoCupom(qtd, medida) {
  if (!medida) return `${formatarQtd(qtd, null)}x`;
  const n = Number(qtd);
  return `${(Number.isFinite(n) ? n : 1).toFixed(3).replace(".", ",")} ${medida}`;
}

// Linha do cupom do cliente: "2 x 13,00" ou "1,200 kg x 39,90/kg".
export function qtdVezesPreco(qtd, precoStr, medida) {
  return medida ? `${quantidadeNoCupom(qtd, medida)} x ${precoStr}/${medida}` : `${formatarQtd(qtd, null)} x ${precoStr}`;
}

export function shouldPrintPreparoForProduct(product, modulo) {
  const pc = getProductPrintConfig(product);
  if (!pc || !Array.isArray(pc.preparo_modules)) return true;
  if (pc.preparo_modules.length === 0) return false;  // todos desmarcados = nunca imprime
  const m = String(modulo || '').trim();
  if (!m) return true;
  // Mesma equivalência do servidor (print_service.modulo_do_preparo): a tela do
  // produto só tem Balcão/Delivery/Mesas/Cardápio — Cardápio público segue o
  // Cardápio, Totem o Balcão, Mesa QR as Mesas e iFood o Delivery (27/09/2026)
  const EQUIVALENTE = { CardapioPublico: 'Cardapio', Totem: 'Balcao', MesaQR: 'Mesas', 'Mesa QR': 'Mesas' };
  const moduloNorm = EQUIVALENTE[m] || (m.toLowerCase() === 'ifood' ? 'Delivery' : m);
  return pc.preparo_modules.includes(moduloNorm);
}

/**
 * Extrai o "apelido" da mesa pra impressao no cupom.
 * - 'Mesa 1A' -> '1A'
 * - 'Mesa 11F' -> '11F'
 * - 'Mesa Reservada' -> 'RESERVADA'
 * - null/vazio -> fallback pra `numero` padded em 2 digitos
 *
 * @param {string} nome - mesa.nome
 * @param {number} numero - mesa.numero (fallback)
 * @returns {string} label curto pra impressao
 */
export function mesaLabelPrint(nome, numero) {
  if (nome && typeof nome === 'string') {
    const m = nome.match(/^Mesa\s+(.+)$/i);
    if (m) {
      const v = m[1].trim();
      if (/^\d+$/.test(v)) return v.padStart(2, '0');
      return v.toUpperCase();
    }
    // Nome custom sem prefixo "Mesa "
    return nome.trim().toUpperCase();
  }
  return String(numero || 0).padStart(2, '0');
}

/**
 * Le o font_scale atual do cache de impressao (ou busca do backend).
 * Cap [0.5, 2.0]. Default 1.0 quando nao configurado.
 */
export async function getFontScale() {
  const cfg = await getImpressaoConfig();
  const fs = Number(cfg?.font_scale);
  if (!Number.isFinite(fs) || fs <= 0) return 1.0;
  return Math.max(0.5, Math.min(2.0, fs));
}

/**
 * Constroi o snippet <style> que aplica zoom no body — escala TODA a fonte/layout do cupom.
 * Usa `zoom` (suportado em Chromium/Electron) que respeita @media print.
 * Quando scale = 1.0, retorna string vazia (zero overhead).
 */
export function buildFontScaleStyle(scale) {
  const s = Number(scale);
  if (!Number.isFinite(s) || Math.abs(s - 1.0) < 0.001) return "";
  return `body{zoom:${s};}`;
}

/**
 * Monta objeto empresaData para enviar ao Print Connector (ESC/POS nativo).
 * @param {Object} company - Dados da empresa (company_name, slogan, etc.)
 * @param {string} logoUrl - URL completa do logo
 */
export function buildEmpresaPayload(company, logoUrl) {
  return {
    nome: company.company_name || company.fantasy_name || "Núcleo",
    slogan: company.slogan || "",
    endereco: company.address || "",
    cnpj: company.cnpj || "",
    telefone: company.phone || "",
    logo_url: logoUrl || "",
  };
}

/**
 * Partes do pagamento dividido pro cupom: [{label, amount, ...}], uma linha por
 * forma (dono, 03/10/2026: "parar de sair múltiplo"). Aceita as partes como
 * lista, como texto JSON (pedido lido do banco) ou o `pagamento_partes` que a
 * API anexa ({nome, valor}). Sem partes: null — o cupom mostra a forma única.
 */
export function partesDoPagamento(pedido) {
  let lista = pedido?.pagamentos;
  if (typeof lista === "string") {
    try { lista = JSON.parse(lista || "null"); } catch { lista = null; }
  }
  if (Array.isArray(lista) && lista.length > 0) return lista;
  const partes = pedido?.pagamento_partes;
  if (String(pedido?.forma_pagamento || "").toLowerCase() === "multiplo" && Array.isArray(partes) && partes.length > 0) {
    return partes.map((pt) => ({ label: pt.nome, amount: pt.valor }));
  }
  return null;
}
